import { Dialog, Tooltip, AlertColor } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import styles from './access.module.css';
import { useLocation, useParams } from 'react-router-dom';

import { fontSize, palette } from '../..';
import { ffButtonStyle } from '../../Components/Controls/muiStyles';
import HoverButton from '../Buttons/HoverButton';
import FetchData from '../ServerCall/FetchData';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';

type User = {
  id: string;
  name: string;
  isGroup: boolean;
  emailId?: string;
  role?: 'owner' | 'moderator' | 'creator' | 'custom_creator' | 'player';
  customPermissions?: [
    {
      contentTypeId: number;
      privilegeId: number;
    },
  ];
};

const AddNewAccessModal = ({
  modalContent,
  searchQuery,
  sortField,
  addedUser,
  selectedUser,
  isModalOpen,
  isModalForContentAccess,
  accessList,

  setSearchQuery,
  setSortField,
  setModalContent,
  setSelectedUser,

  handleAdd,
  closeModal,
}: {
  isModalForContentAccess: boolean;
  selectedUser: User | null;
  addedUser: User | null;
  isModalOpen: boolean;
  searchQuery: string;
  modalContent: string;
  sortField: {
    key: string;
    direction: 'asc' | 'desc';
  };
  accessList: any[];
  setModalContent: (modalContent: string) => void;
  setSelectedUser: React.Dispatch<React.SetStateAction<any>>;
  setSearchQuery: (searchQuery: string) => void;
  handleAdd: () => void;
  setSortField: React.Dispatch<
    React.SetStateAction<{
      key: string;
      direction: 'asc' | 'desc';
    }>
  >;

  closeModal: () => void;
}) => {
  const [modalFetchData, setModalFetchData] = useState<any>([]);
  const { parentId } = useParams();
  const contentId = useParams().parentId;
  const workspaceId = useLocation().state.parentId;
  const [alert, setAlert] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);

  const showAlert = (message: string, severity: AlertColor) => {
    setAlert({
      severity,
      message,
    });
    setTimeout(() => {
      setAlert(null);
    }, 3000);
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [closeModal]);

  useEffect(() => {
    FetchData({
      requestType: 'noData',
      url: isModalForContentAccess
        ? `access/user-groups/content?workspaceId=${workspaceId}&contentId=${contentId}`
        : `access/user-groups/workspace?workspaceId=${parentId}`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (response.status) {
        setModalFetchData(response.data);
      }
    });
  }, [accessList]);

  const handleSortModal = (key: string) => {
    setSortField((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleUserSelection = (userId: any, isGroup: any, userName: any, emailId?: string) => {
    setSelectedUser({ id: userId, isGroup: isGroup, name: userName, emailId: emailId });
  };

  const [sortedAndFilteredModalData, setSortedAndFilteredModalData] = useState<any[]>([]);

  useEffect(() => {
    let processedData = [...modalFetchData];

    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();

      processedData = processedData.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(lowerCaseQuery);
        const emailMatch = item.emailId
          ? item.emailId.toLowerCase().includes(lowerCaseQuery)
          : false;
        return nameMatch || emailMatch;
      });
    }

    if (sortField.key) {
      processedData.sort((a, b) => {
        let aValue, bValue;
        if (sortField.key === 'name') {
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
        } else if (sortField.key === 'emailId') {
          aValue = a.emailId ? a.emailId.toLowerCase() : '';
          bValue = b.emailId ? b.emailId.toLowerCase() : '';
        }
        if (aValue < bValue) {
          return sortField.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortField.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedAndFilteredModalData(processedData);
  }, [modalFetchData, sortField, searchQuery]);

  const getSortIconModal = (key: any) => {
    if (sortField.key === key) {
      return sortField.direction === 'asc' ? (
        <img className="headerIcon-up-modal" src="/sort-up.png" alt="Ascending" />
      ) : (
        <img className="headerIcon-down-modal" src="/sort-down.png" alt="Descending" />
      );
    }
    return <img className="headerIcon-modal" src="/sort.png" alt="Default" />;
  };

  return (
    <Dialog open={isModalOpen} onClose={closeModal} maxWidth="md">
      <div className="accessListModal">
        <div className="accessListModalContent">
          <div className="accessListHeaderSection">
            <div className="accessListHeaderClose">
              <Tooltip
                title="Close"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <CloseIcon
                  onClick={closeModal}
                  sx={{
                    fontSize: 'large',
                    color: '#545454',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'red',
                    },
                  }}
                />
              </Tooltip>
            </div>

            <h3 className="access-modal-title">
              {modalContent === 'User' ? 'Select User' : 'Select Group'}
            </h3>

            <div className={styles.adminListModalTabs}>
              <div className={styles.tabContainer}>
                <div
                  onClick={() => setModalContent('User')}
                  className={`${styles.tab} ${modalContent === 'User' ? styles.activeTab : ''}`}
                >
                  Users
                </div>

                <div
                  onClick={() => setModalContent('Groups')}
                  className={`${styles.tab} ${modalContent === 'Groups' ? styles.activeTab : ''}`}
                >
                  Groups
                </div>
              </div>
            </div>

            {modalContent === 'User' && (
              <div className="accessListSearchContainer">
                {/* User Search */}
                <input
                  type="text"
                  placeholder="Search users"
                  className="accessListSearchInput"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <img src="/glass.png" alt="Search Icon" className="accessListSearchIcon" />
              </div>
            )}
            {modalContent === 'Groups' && (
              <div className="adminListSearchContainer">
                {/* Group Search */}
                <input
                  type="text"
                  placeholder="Search groups"
                  className="accessListSearchInput"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <img src="/glass.png" alt="Search Icon" className="accessListSearchIcon" />
              </div>
            )}
          </div>

          <div className="accessListTableContainer">
            {modalContent === 'User' && (
              <table className={`${styles.accessListUserList} accessListUserList`}>
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                    <th onClick={() => handleSortModal('name')}>
                      Name <span className="icon-wrapper-modal">{getSortIconModal('name')}</span>
                    </th>
                    <th onClick={() => handleSortModal('emailId')}>
                      Email{' '}
                      <span className="icon-wrapper-modal">{getSortIconModal('emailId')}</span>
                    </th>
                  </tr>
                </thead>

                {sortedAndFilteredModalData.length === 0 && searchQuery.length !== 0 ? (
                  <div className="modal-content-no-user-info">
                    <p
                      className="loading-default-text"
                      style={{
                        fontSize: fontSize.medium,
                        color: palette.primary.contrastText,
                      }}
                    >
                      No user found.
                    </p>
                  </div>
                ) : (
                  <tbody>
                    {sortedAndFilteredModalData
                      .filter((user: any) => !user.isGroup && !(addedUser?.id === user.id))
                      .map((user: any) => (
                        <tr key={user.id} className={styles.accessUserItem}>
                          <td>
                            <input
                              type="radio"
                              className={styles.radioButton}
                              checked={selectedUser?.id === user.id}
                              onChange={() =>
                                handleUserSelection(user.id, user.isGroup, user.name, user.emailId)
                              }
                            />
                          </td>
                          <td>
                            <img
                              src={
                                user.profileImage
                                  ? `data:image/jpeg;base64,${user.profileImage}`
                                  : '/default.png'
                              }
                              alt="Users Icon"
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                              }}
                            />
                          </td>
                          <td>{user.name}</td>
                          <td>{user.emailId}</td>
                        </tr>
                      ))}
                  </tbody>
                )}
                <div
                  style={{
                    marginTop: '6px',
                    color: palette.primary.contrastText,
                  }}
                >
                  Admins and users already added are not shown in the list
                </div>
              </table>
            )}

            {modalContent === 'Groups' && (
              <table className={`${styles.accessListGroupList} accessListGroupList`}>
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                    <th onClick={() => handleSortModal('name')}>
                      Name <span className="icon-wrapper-modal">{getSortIconModal('name')}</span>
                    </th>
                  </tr>
                </thead>

                {sortedAndFilteredModalData.length === 0 && searchQuery.length !== 0 ? (
                  <div className="modal-content-no-user-info">
                    <p
                      className="loading-default-text"
                      style={{
                        fontSize: fontSize.medium,
                        color: palette.primary.contrastText,
                      }}
                    >
                      No group found.
                    </p>
                  </div>
                ) : (
                  <tbody>
                    {sortedAndFilteredModalData
                      .filter((group: any) => group.isGroup && !(addedUser?.id === group.id))
                      .map((group: any) => (
                        <tr key={group.id} className={styles.accessUserItem}>
                          <td>
                            <input
                              type="radio"
                              className={styles.radioButton}
                              checked={selectedUser?.id === group.id}
                              onChange={() =>
                                handleUserSelection(group.id, group.isGroup, group.name)
                              }
                            />
                          </td>
                          <td>
                            <img
                              src="/groups.png"
                              alt="Group Icon"
                              style={{ width: '17px', height: '17px' }}
                            />
                          </td>
                          <td>{group.name}</td>
                        </tr>
                      ))}
                  </tbody>
                )}
                <div
                  style={{
                    marginTop: '6px',
                    color: palette.primary.contrastText,
                  }}
                >
                  Admins and groups already added are not shown in the list
                </div>
              </table>
            )}
          </div>

          <div className="accessListFooterSection">
            <HoverButton
              onClick={() => {
                if (!selectedUser) {
                  showAlert('No user selected to add as access', 'error');
                } else {
                  handleAdd();
                }
              }}
              text="Add"
              color="secondary.light"
              hoverColor="secondary.contrastText"
              backgroundColor="secondary.contastText"
              hoverBackgroundColor="secondary.light"
              sx={{
                ...ffButtonStyle,
                border: `1px solid ${palette.secondary.light}`,
                fontSize: fontSize.medium,
                lineHeight: 'normal',
                borderRadius: '5px',
                padding: '10px 20px',
                margin: '0',
              }}
              transitionTime="0.2s"
            />
          </div>
        </div>
      </div>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </Dialog>
  );
};

export default AddNewAccessModal;
